import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, type ExportPaket, type Posten, type Stelle } from '../api/client';
import { Lageskizze } from '../components/Lageskizze';
import { Meldung, StatusPill, fehlerText } from '../components/ui';
import {
  ART_TEXT,
  MASSNAHME_TEXT,
  STATUS_TEXT,
  datum,
  datumZeit,
  material,
  uhrzeit,
} from '../format';
import { MapView } from '../map/MapView';
import type { KartenPunkt } from '../map/punkteLayer';
import { link } from '../router';
import { stellenNummern } from './Planung';

type Quelle = { planversion: string } | { token: string };

/**
 * Druckansichten (CSS @page, A3/A4). Alles aus dem Export-Paket – kein Kartendienst nötig außer für das Kartenbild.
 * Jedes Blatt trägt „Planungshilfe“ und die OSM-Attribution; Status immer mit Text, daher graustufentauglich.
 */
export function Druck({ quelle, art }: { quelle: Quelle; art: string }) {
  const [paket, setPaket] = useState<ExportPaket | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    const laden = 'token' in quelle ? api.geteilt(quelle.token) : api.export(quelle.planversion);
    laden.then(setPaket).catch((e: unknown) => setFehler(fehlerText(e)));
  }, [quelle]);

  useEffect(() => {
    document.body.classList.add('druckmodus');
    const stil = document.createElement('style');
    stil.textContent = `@page { size: ${art === 'lagekarte' ? 'A3 landscape' : 'A4 portrait'}; margin: 12mm; }`;
    document.head.append(stil);
    return () => {
      document.body.classList.remove('druckmodus');
      stil.remove();
    };
  }, [art]);

  if (fehler) return <Meldung art="fehler">{fehler}</Meldung>;
  if (!paket) return <p className="laedt">Lädt …</p>;

  const basis = 'token' in quelle ? ['geteilt', quelle.token] : ['druck', quelle.planversion];
  const v = paket.veranstaltung;

  return (
    <div className="druck">
      <div className="druck__leiste nicht-drucken">
        <strong>{v.titel}</strong>
        <nav>
          {(
            ['uebersicht', 'lagekarte', 'postenliste', 'postenkarten', 'einsatzbefehl'] as const
          ).map((a) => (
            <a key={a} href={link(...basis, a)} aria-current={a === art ? 'page' : undefined}>
              {
                {
                  uebersicht: 'Übersicht',
                  lagekarte: 'Lagekarte',
                  postenliste: 'Postenliste',
                  postenkarten: 'Postenkarten',
                  einsatzbefehl: 'Einsatzbefehl',
                }[a]
              }
            </a>
          ))}
        </nav>
        {art !== 'uebersicht' && (
          <button type="button" className="knopf knopf--primaer" onClick={() => window.print()}>
            Als PDF drucken
          </button>
        )}
      </div>
      {paket.geteilt && <p className="nicht-drucken gedaempft">Geteilter Plan – nur lesen.</p>}
      {art === 'lagekarte' && <Lagekarte p={paket} />}
      {art === 'postenliste' && <Postenliste p={paket} />}
      {art === 'postenkarten' && <Postenkarten p={paket} />}
      {art === 'einsatzbefehl' && <Einsatzbefehl p={paket} />}
      {art === 'uebersicht' && (
        <Blatt p={paket} titel="Planübersicht">
          <Kopfdaten p={paket} />
          <p>Bitte eine Ansicht oben auswählen.</p>
          <StellenTabelle p={paket} />
        </Blatt>
      )}
    </div>
  );
}

function Blatt({
  p,
  titel,
  children,
  quer,
}: {
  p: ExportPaket;
  titel: string;
  children: ReactNode;
  quer?: boolean;
}) {
  return (
    <section className={`blatt${quer ? ' blatt--quer' : ''}`}>
      <header className="blatt__kopf">
        <span>{titel}</span>
        <span>
          {p.veranstaltung.titel} · {datumZeit(p.veranstaltung.startzeit)}
        </span>
      </header>
      <div className="blatt__inhalt">{children}</div>
      <footer className="blatt__fuss">
        <span>{p.hinweis}</span>
        <span>
          Kartendaten {p.attribution} · Stand {datumZeit(p.analyse?.erstellt_am ?? p.erstellt)}
          {p.anordnung?.status !== 'freigegeben' && ' · VORGABE NICHT FREIGEGEBEN'}
        </span>
      </footer>
    </section>
  );
}

function Kopfdaten({ p }: { p: ExportPaket }) {
  const v = p.veranstaltung;
  const a = p.anordnung;
  return (
    <dl className="kopfdaten">
      <dt>Veranstaltung</dt>
      <dd>
        {v.titel} ({ART_TEXT[v.art] ?? v.art}), {v.teilnehmer} Teilnehmende
        {v.besonderheiten.length > 0 && ` – ${v.besonderheiten.join(', ')}`}
      </dd>
      <dt>Start / Ende</dt>
      <dd>
        {datumZeit(v.startzeit)} – Zugende am Ziel ca. {uhrzeit(p.analyse?.zug.ende)}
      </dd>
      <dt>Anordnung</dt>
      <dd>
        {a
          ? `${a.behoerde ?? ''} ${a.aktenzeichen ? `Az. ${a.aktenzeichen}` : ''} ${a.datum ? `vom ${datum(a.datum)}` : ''} – ${a.status === 'freigegeben' ? `freigegeben (${a.freigegeben_von ?? ''})` : 'NICHT freigegeben'}`
          : 'nicht erfasst'}
      </dd>
      {p.analyse && (
        <>
          <dt>Zugmodell (Annahme)</dt>
          <dd>
            {p.analyse.zug.geh_kmh} km/h, Zuglänge ca. {Math.round(p.analyse.zug.laenge_m)} m, Route{' '}
            {Math.round(p.analyse.zug.route_laenge_m)} m, Vorlauf {p.analyse.zug.vorlauf_min} min,
            Nachlauf {p.analyse.zug.nachlauf_min} min
          </dd>
        </>
      )}
    </dl>
  );
}

function useStellen(p: ExportPaket): [Stelle, number][] {
  return useMemo(() => {
    const st = p.analyse?.stellen ?? [];
    return stellenNummern(st).sort((a, b) => a[1] - b[1]);
  }, [p]);
}

function marke(s: Stelle, nr: number): string {
  return s.kennung && s.herkunft !== 'erkannt' ? s.kennung : String(nr);
}

function StellenTabelle({ p }: { p: ExportPaket }) {
  const stellen = useStellen(p);
  return (
    <table className="tabelle tabelle--druck">
      <thead>
        <tr>
          <th>Nr.</th>
          <th>Stelle</th>
          <th>Maßnahme / Fundstelle</th>
          <th>Besetzen – Freigabe</th>
          <th>Status</th>
          <th>Befund</th>
        </tr>
      </thead>
      <tbody>
        {stellen.map(([s, nr]) => (
          <tr key={s.id}>
            <td>{marke(s, nr)}</td>
            <td>{s.bezeichnung}</td>
            <td>
              {s.herkunft === 'erkannt'
                ? 'nicht angeordnet'
                : s.massnahme
                  ? MASSNAHME_TEXT[s.massnahme]
                  : ''}{' '}
              {s.fundstelle && `(${s.fundstelle})`}
            </td>
            <td>
              {s.zeiten ? `${uhrzeit(s.zeiten.besetzen)} – ${uhrzeit(s.zeiten.freigabe)}` : '–'}
            </td>
            <td>
              <StatusPill status={s.status} />
            </td>
            <td>
              {s.befunde
                .filter((b) => b.code !== 'OK')
                .map((b) => (b.bestaetigt ? `(zur Kenntnis) ${b.begruendung}` : b.begruendung))
                .join(' ')}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Lagekarte({ p }: { p: ExportPaket }) {
  const stellen = useStellen(p);
  const punkte: KartenPunkt[] = stellen.map(([s, nr]) => ({
    id: s.id,
    position: s.position.coordinates,
    status: s.status,
    label: marke(s, nr),
    vorgabe: s.herkunft === 'angeordnet',
  }));
  return (
    <Blatt p={p} titel="Lagekarte" quer>
      <div className="lagekarte">
        <div className="lagekarte__karte">
          <MapView statisch route={p.route?.geometry.coordinates ?? null} punkte={punkte} />
        </div>
        <div className="lagekarte__legende">
          <Kopfdaten p={p} />
          <h3>Legende</h3>
          <ul className="legende">
            <li>Doppelter Ring = angeordnete Stelle (Vorgabe)</li>
            {(['rot', 'gelb', 'gruen', 'polizei'] as const).map((st) => (
              <li key={st}>
                <StatusPill status={st} text={STATUS_TEXT[st]} />
              </li>
            ))}
          </ul>
          <table className="tabelle tabelle--druck tabelle--kompakt">
            <tbody>
              {stellen.map(([s, nr]) => (
                <tr key={s.id}>
                  <td>{marke(s, nr)}</td>
                  <td>{s.bezeichnung}</td>
                  <td>
                    {s.zeiten ? `${uhrzeit(s.zeiten.besetzen)}–${uhrzeit(s.zeiten.freigabe)}` : ''}
                  </td>
                  <td>{STATUS_TEXT[s.status]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Blatt>
  );
}

function einheitText(p: ExportPaket, po: Posten): string {
  const e = p.einheiten.find((x) => x.id === po.einheit_id);
  return e ? `${e.name}${e.funkrufname ? ` (${e.funkrufname})` : ''}` : 'NICHT ZUGEORDNET';
}

function Postenliste({ p }: { p: ExportPaket }) {
  const stellen = useStellen(p);
  return (
    <Blatt p={p} titel="Postenliste">
      <table className="tabelle tabelle--druck">
        <thead>
          <tr>
            <th>Nr.</th>
            <th>Stelle</th>
            <th>Besetzen ab</th>
            <th>Freigabe ca.</th>
            <th>Einheit</th>
            <th>Pers.</th>
            <th>Abschnitt</th>
            <th>Auftrag</th>
            <th>Material</th>
          </tr>
        </thead>
        <tbody>
          {stellen.flatMap(([s, nr]) =>
            p.posten
              .filter((po) => po.sperrstelle_id === s.id)
              .map((po) => (
                <tr key={po.id}>
                  <td>{marke(s, nr)}</td>
                  <td>{s.bezeichnung}</td>
                  <td>{uhrzeit(po.besetzen_von)}</td>
                  <td>{uhrzeit(po.freigabe_ca)}</td>
                  <td>{einheitText(p, po)}</td>
                  <td>{po.personen}</td>
                  <td>
                    {p.einsatzabschnitte.find((a) => a.id === po.einsatzabschnitt_id)?.name ?? '–'}
                  </td>
                  <td>{po.auftrag}</td>
                  <td>{material(po.material)}</td>
                </tr>
              )),
          )}
        </tbody>
      </table>
      {p.posten.length === 0 && <p>Noch keine Posten eingeplant.</p>}
      <p>
        Summe: {p.posten.reduce((n, x) => n + x.personen, 0)} Personen an {p.posten.length} Posten.
        Mindestbedarf gleichzeitig laut Modell: {p.analyse?.summen.personal_mindestens ?? '–'}{' '}
        Personen (Annahme).
      </p>
    </Blatt>
  );
}

export function PostenkarteInhalt({ p, po }: { p: ExportPaket; po: Posten }) {
  const stellen = p.analyse?.stellen ?? [];
  const s = stellen.find((x) => x.id === po.sperrstelle_id);
  const nummern = new Map(stellenNummern(stellen).map(([x, n]) => [x.id, n]));
  const abschnitt = p.einsatzabschnitte.find((a) => a.id === po.einsatzabschnitt_id);
  if (!s) return <p>Stelle nicht mehr im Plan.</p>;
  return (
    <div className="postenkarte">
      <h2>
        {marke(s, nummern.get(s.id) ?? 0)} – {s.bezeichnung}
      </h2>
      {p.route && (
        <Lageskizze
          route={p.route.geometry.coordinates}
          ziel={s.position.coordinates}
          andere={stellen
            .filter((x) => x.id !== s.id)
            .map((x) => ({
              position: x.position.coordinates,
              label: marke(x, nummern.get(x.id) ?? 0),
            }))}
        />
      )}
      <dl className="postenkarte__daten">
        <dt>Auftrag</dt>
        <dd>{po.auftrag ?? '–'}</dd>
        <dt>Besetzen ab</dt>
        <dd className="gross">{uhrzeit(po.besetzen_von)} Uhr</dd>
        <dt>Freigabe ca.</dt>
        <dd className="gross">{uhrzeit(po.freigabe_ca)} Uhr</dd>
        <dt>Maßnahme</dt>
        <dd>
          {s.herkunft === 'erkannt'
            ? 'Keine angeordnete Maßnahme – keine Verkehrsregelung'
            : s.massnahme
              ? MASSNAHME_TEXT[s.massnahme]
              : '–'}
          {s.sperrzeit_von &&
            ` · Sperrzeit ${uhrzeit(s.sperrzeit_von)}–${uhrzeit(s.sperrzeit_bis)}`}
        </dd>
        <dt>Einheit / Personen</dt>
        <dd>
          {einheitText(p, po)} · {po.personen}
        </dd>
        <dt>Material</dt>
        <dd>{material(po.material)}</dd>
        <dt>Abschnittsleitung</dt>
        <dd>{abschnitt ? `${abschnitt.name}: ${abschnitt.leiter ?? '–'}` : '–'}</dd>
        <dt>Funk</dt>
        <dd>
          {abschnitt?.funkrufname ?? '–'}
          {abschnitt?.gespraechsgruppe && `, GG ${abschnitt.gespraechsgruppe}`}
        </dd>
      </dl>
    </div>
  );
}

function Postenkarten({ p }: { p: ExportPaket }) {
  const stellen = useStellen(p);
  const sortiert = stellen.flatMap(([s]) => p.posten.filter((po) => po.sperrstelle_id === s.id));
  if (sortiert.length === 0)
    return (
      <Blatt p={p} titel="Postenkarten">
        <p>Noch keine Posten eingeplant.</p>
      </Blatt>
    );
  return (
    <>
      {sortiert.map((po) => (
        <Blatt key={po.id} p={p} titel="Postenkarte">
          <PostenkarteInhalt p={p} po={po} />
        </Blatt>
      ))}
    </>
  );
}

function Einsatzbefehl({ p }: { p: ExportPaket }) {
  const a = p.analyse;
  return (
    <Blatt p={p} titel="Einsatzbefehl (Gerüst)">
      <p className="gedaempft">
        Gerüst aus den Planungsdaten – von der Einsatzleitung zu ergänzen und zu verantworten.
      </p>
      <h3>1. Lage</h3>
      <Kopfdaten p={p} />
      <h3>2. Auftrag</h3>
      <p>
        Sicherung des Umzugs „{p.veranstaltung.titel}“ entlang der Route (
        {Math.round(a?.zug.route_laenge_m ?? 0)} m) gemäß verkehrsbehördlicher Anordnung
        {p.anordnung?.aktenzeichen ? ` (Az. ${p.anordnung.aktenzeichen})` : ''}.
      </p>
      <h3>3. Durchführung</h3>
      <h4>Einsatzabschnitte</h4>
      {p.einsatzabschnitte.length === 0 ? (
        <p>Keine Abschnitte gebildet.</p>
      ) : (
        <ul>
          {p.einsatzabschnitte.map((ab) => (
            <li key={ab.id}>
              {ab.name}: Leitung {ab.leiter ?? '–'}, {ab.funkrufname ?? '–'} –{' '}
              {p.posten.filter((x) => x.einsatzabschnitt_id === ab.id).length} Posten
            </li>
          ))}
        </ul>
      )}
      <h4>Stellen</h4>
      <StellenTabelle p={p} />
      <h4>Auflagen</h4>
      <ul>
        {(a?.auflagen ?? []).map((x) => (
          <li key={x.id}>
            {x.text} {x.fundstelle && `(${x.fundstelle})`} – {STATUS_TEXT[x.status]}:{' '}
            {x.befund.begruendung}
          </li>
        ))}
      </ul>
      <h3>4. Versorgung</h3>
      <p>
        Material laut Planung: {material(a?.summen.material_bedarf)}. ____________________________
      </p>
      <h3>5. Führung und Kommunikation</h3>
      <p>
        Einsatzleitung: ____________________ · Funkrufname: ____________________ · Gesprächsgruppe:
        __________
      </p>
      <p>
        Offene Punkte: {a ? `${a.zaehler.rot} rot, ${a.zaehler.gelb_offen} gelb` : 'nicht geprüft'}
      </p>
    </Blatt>
  );
}
